/**
 * Scroll-linked bottom-nav reveal on the narrow (single-column) concept page.
 *
 * The concept page (`ConceptEditor`) writes a 0..1 progress into the
 * `--bottom-bar-reveal` CSS custom property on `<html>` from a rAF-throttled
 * window-scroll listener: `0` at the top (nav hidden — only the Generate
 * capsule shows) → `1` once scrolled ~half the viewport (nav fully revealed).
 * The fixed `MobileNavigation` island and the fixed Generate capsules read the
 * property through `transform: translateY(calc(...))`, so they slide with the
 * scroll finger and never re-render React on scroll. Off the concept page the
 * property is unset and the `, 1` fallback resolves the transform to 0 — no
 * movement anywhere else.
 *
 * Mirrors Apple `BottomBarRevealState.shared` / Android `BottomBarRevealState`.
 */
export const BOTTOM_BAR_REVEAL_VAR = '--bottom-bar-reveal';

/**
 * Scroll distance (px) over which the reveal plays out at the END of the page:
 * the nav/Generate capsule stay put until the user is within this many px of the
 * bottom, then slide in so the nav is fully revealed at the last line of content.
 * Sized roughly to nav + capsule + gap. Mirrors Apple `bottomBarRevealWindow`.
 */
export const BOTTOM_BAR_REVEAL_WINDOW_PX = 220;

/**
 * Bottom padding the stacked concept page reserves under the settings so the
 * fixed Generate capsule + nav island never cover the last rows (`pb-36` on the
 * ConceptEditor tab wrappers = 9rem; capsule top sits ~124px up: nav bottom 12
 * + island ~61 + capsule anchor gap + h-10 capsule). It is dead space, not content, so the
 * reveal must exclude it: Apple/Android anchor the reveal to the CONTENT's
 * bottom edge (their scroll bottom margin sits outside the measured content),
 * while `document.scrollHeight` includes this padding. Without subtracting it
 * the nav finishes sliding in only at the very end of the empty strip — well
 * past the last line of content.
 */
export const BOTTOM_BAR_REVEAL_END_INSET_PX = 144;

/** Slides the floating nav island fully off the bottom edge at progress 0. */
export const NAV_REVEAL_TRANSFORM = `translateY(calc((1 - var(${BOTTOM_BAR_REVEAL_VAR}, 1)) * 96px))`;

/**
 * Drops the Generate capsule from its resting spot above the nav
 * (`bottom-[84px]`) down toward the screen edge as the nav hides, so it reads
 * as "instead of the nav, just the Generate button" at the top of the page.
 */
export const FOOTER_REVEAL_TRANSFORM = `translateY(calc((1 - var(${BOTTOM_BAR_REVEAL_VAR}, 1)) * 76px))`;

/**
 * Computes the END-anchored scroll progress (0..1) from the current window
 * scroll: `0` until the last [BOTTOM_BAR_REVEAL_WINDOW_PX] before the bottom,
 * then ramps to `1` at the last line of content. Shared by the concept page's
 * scroll writer and the sub-panel hold's release path so the formula lives once.
 */
export function computeBottomBarRevealProgress(): number {
  if (typeof window === 'undefined') return 1;
  // Anchor to the content's bottom edge, not the padded document bottom —
  // mirrors Apple/Android, which measure the last content element directly.
  const distanceToBottom =
    document.documentElement.scrollHeight -
    BOTTOM_BAR_REVEAL_END_INSET_PX -
    window.scrollY -
    window.innerHeight;
  return Math.min(1, Math.max(0, 1 - distanceToBottom / BOTTOM_BAR_REVEAL_WINDOW_PX));
}

/**
 * While a stacked settings sub-panel is open the bar must stay hidden, but the
 * concept page's scroll writer keeps running (a mid-panel resize — e.g. the
 * keyboard opening on Requirements/Prompts — would otherwise recompute a
 * non-zero progress and flash the nav in over the sub-panel). This module flag
 * lets [setBottomBarReveal] ignore scroll-linked writes while held, so the
 * sub-panel is the sole authority — mirroring Apple (sub-panel never drives the
 * reveal) and Android (`BottomBarRevealSubPanelGuard`).
 */
let bottomBarHidden = false;

/** Hold the bar hidden regardless of scroll while a stacked sub-panel is open. */
export function holdBottomBarHidden(): void {
  bottomBarHidden = true;
  setBottomBarReveal(0);
}

/** Release the hold and hand the bar back to the scroll-linked writer. */
export function releaseBottomBarHidden(): void {
  bottomBarHidden = false;
  setBottomBarReveal(computeBottomBarRevealProgress());
}

/**
 * Set the reveal progress (0..1) the fixed bottom elements follow. Pass `null`
 * to clear it (restores the fully-shown state via the `, 1` fallback) — the
 * concept page MUST clear it on unmount so the global nav is never stranded
 * hidden on another route. Clearing also drops any active sub-panel hold, so a
 * stale hold can't leak onto the next route.
 */
export function setBottomBarReveal(progress: number | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (progress === null) {
    bottomBarHidden = false;
    root.style.removeProperty(BOTTOM_BAR_REVEAL_VAR);
    return;
  }
  // A sub-panel hold pins the bar hidden; scroll-linked writes are suppressed.
  root.style.setProperty(
    BOTTOM_BAR_REVEAL_VAR,
    bottomBarHidden ? '0' : String(progress)
  );
}
